import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Settings,
  Bell,
  Target,
  Users,
  TrendingUp,
  RefreshCcw,
  Shield,
  Eye,
  Megaphone,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import QawaemStatsCard from '../../products/saudi/components/QawaemStatsCard';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import BrandCurrencySelect from '../../shared/components/BrandCurrencySelect';
import ModuleConfigPageLayout from '../../shared/components/settings/ModuleConfigPageLayout';
import { syncCrmConfigToSettings } from './useCrmConfig';
import { DEFAULT_CRM_CONFIG, normalizeCrmConfig } from './crmConfigDefaults';

const NS = 'crmConfigPage';
const API = '/crm/config';

function CheckboxField({ checked, onChange, label }) {
  return (
    <label className="flex w-full flex-row-reverse items-center justify-end gap-2 text-white cursor-pointer text-right">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="rounded" />
      {label}
    </label>
  );
}

export default function CrmConfigPage() {
  const { t } = useTranslation();
  const { settings, patchSettings } = useSettings();
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const p = (key, opts) => t(`modules.crm.pages.${NS}.${key}`, opts);
  const qawaem = isSaudiBrand();

  const [config, setConfig] = useState(DEFAULT_CRM_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState('leads_pipeline');

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await localApiAdapter.get(API);
      const raw = response?.data !== undefined ? response.data : response;
      const merged = normalizeCrmConfig(raw || {});
      setConfig(merged);
      syncCrmConfigToSettings(merged, patchSettings, settingsRef.current);
    } catch (error) {
      console.error('Failed to load CRM config:', error);
      toast.error(t(`modules.crm.pages.${NS}.load_error`));
      const fallback = normalizeCrmConfig({});
      setConfig(fallback);
      syncCrmConfigToSettings(fallback, patchSettings, settingsRef.current);
    } finally {
      setLoading(false);
    }
  }, [patchSettings, t]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const reloadConfig = () => {
    void loadConfig();
  };

  const updateConfig = (key, value) => {
    setConfig((prev) => {
      const next = normalizeCrmConfig({ ...prev, [key]: value });
      syncCrmConfigToSettings(next, patchSettings, settingsRef.current);
      return next;
    });
  };

  const activeNotifications = [
    config.notifyNewLead,
    config.notifyDealWon,
    config.notifyGoalAchieved,
    config.notifyFollowUpDue,
    config.emailNotifications,
  ].filter(Boolean).length;

  const stats = [
    {
      titleKey: 'stat_max_leads',
      value: config.maxLeadsPerUser,
      icon: Users,
      variant: 'copper',
    },
    {
      titleKey: 'stat_pipeline',
      value: config.pipelineStages,
      icon: TrendingUp,
      variant: 'forest',
    },
    {
      titleKey: 'stat_view',
      value: p(`view_${config.defaultView}`),
      icon: Eye,
      variant: 'sage',
    },
    {
      titleKey: 'stat_notifications',
      value: `${activeNotifications}/5`,
      icon: Bell,
      variant: 'copper',
    },
    {
      titleKey: 'stat_vip_threshold',
      value: config.vipCustomerThreshold?.toLocaleString?.() ?? config.vipCustomerThreshold,
      icon: Target,
      variant: 'forest',
    },
  ];

  const leadRotationOptions = [
    { value: 'round_robin', label: p('rotation_round_robin') },
    { value: 'manual', label: p('rotation_manual') },
    { value: 'territory', label: p('rotation_territory') },
  ];

  const pipelineOptions = [
    { value: 'sales', label: p('pipeline_sales') },
    { value: 'enterprise', label: p('pipeline_enterprise') },
    { value: 'partner', label: p('pipeline_partner') },
  ];

  const defaultViewOptions = [
    { value: 'pipeline', label: p('view_pipeline') },
    { value: 'list', label: p('view_list') },
    { value: 'dashboard', label: p('view_dashboard') },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = normalizeCrmConfig(config);
      await localApiAdapter.put(API, payload);
      syncCrmConfigToSettings(payload, patchSettings, settingsRef.current);
      setConfig(payload);
      setSaved(true);
      toast.success(p('saved_success'));
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save CRM config:', error);
      toast.error(error.message || p('save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(p('reset_confirm'))) return;
    try {
      await localApiAdapter.post(`${API}/reset`, {});
      const resetConfig = normalizeCrmConfig({});
      setConfig(resetConfig);
      syncCrmConfigToSettings(resetConfig, patchSettings, settingsRef.current);
      toast.success(p('reset_success'));
    } catch (error) {
      console.error('Failed to reset CRM config:', error);
      toast.error(error.message || p('reset_error'));
    }
  };

  const statsGrid = (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {stats.slice(0, 4).map((stat, i) =>
        qawaem ? (
          <QawaemStatsCard
            key={stat.titleKey}
            label={p(stat.titleKey)}
            value={stat.value}
            icon={stat.icon}
            variant={stat.variant}
            delay={i * 0.08}
          />
        ) : (
          <motion.div
            key={stat.titleKey}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <p className="text-white/60 text-sm">{p(stat.titleKey)}</p>
            <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
          </motion.div>
        )
      )}
    </div>
  );

  const tabContent = {
    leads_pipeline: (
      <>
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 space-y-6">
          <h2 className="text-lg font-semibold text-white">{p('section_leads')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_max_leads')}</label>
              <input
                type="number"
                min={1}
                value={config.maxLeadsPerUser}
                onChange={(e) => updateConfig('maxLeadsPerUser', parseInt(e.target.value, 10) || 0)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white"
              />
            </div>
            <div>
              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_lead_rotation')}</label>
              <CustomSelect
                value={config.leadRotation}
                onChange={(v) => updateConfig('leadRotation', v)}
                options={leadRotationOptions}
              />
            </div>
            <div className="flex flex-col gap-3">
              <CheckboxField
                checked={config.leadScoring}
                onChange={(v) => updateConfig('leadScoring', v)}
                label={p('label_lead_scoring')}
              />
              <CheckboxField
                checked={config.autoAssignLeads}
                onChange={(v) => updateConfig('autoAssignLeads', v)}
                label={p('label_auto_assign_leads')}
              />
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 space-y-6">
          <h2 className="text-lg font-semibold text-white">{p('section_pipeline')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_default_pipeline')}</label>
              <CustomSelect
                value={config.defaultPipeline}
                onChange={(v) => updateConfig('defaultPipeline', v)}
                options={pipelineOptions}
              />
            </div>
            <div>
              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_pipeline_stages')}</label>
              <input
                type="number"
                min={1}
                max={20}
                value={config.pipelineStages}
                onChange={(e) => updateConfig('pipelineStages', parseInt(e.target.value, 10) || 1)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white"
              />
            </div>
            <div className="flex flex-col gap-3">
              <CheckboxField
                checked={config.requireWinLossReason}
                onChange={(v) => updateConfig('requireWinLossReason', v)}
                label={p('label_require_win_loss')}
              />
              <CheckboxField
                checked={config.autoMoveStages}
                onChange={(v) => updateConfig('autoMoveStages', v)}
                label={p('label_auto_move_stages')}
              />
            </div>
          </div>
        </div>
      </>
    ),

    goals_thresholds: (
      <>
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 space-y-6">
          <h2 className="text-lg font-semibold text-white">{p('section_goals')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_goal_currency')}</label>
              <BrandCurrencySelect
                value={config.goalCurrency}
                onChange={(e) => updateConfig('goalCurrency', e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-3">
              <CheckboxField
                checked={config.monthlyGoalTracking}
                onChange={(v) => updateConfig('monthlyGoalTracking', v)}
                label={p('label_monthly_goals')}
              />
              <CheckboxField
                checked={config.teamGoals}
                onChange={(v) => updateConfig('teamGoals', v)}
                label={p('label_team_goals')}
              />
              <CheckboxField
                checked={config.individualGoals}
                onChange={(v) => updateConfig('individualGoals', v)}
                label={p('label_individual_goals')}
              />
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 space-y-6">
          <h2 className="text-lg font-semibold text-white">{p('section_thresholds')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_vip_customer_threshold')}</label>
              <input
                type="number"
                min={0}
                value={config.vipCustomerThreshold}
                onChange={(e) => updateConfig('vipCustomerThreshold', parseInt(e.target.value, 10) || 0)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white"
              />
            </div>
            <div>
              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_high_value_lead_threshold')}</label>
              <input
                type="number"
                min={0}
                value={config.highValueLeadThreshold}
                onChange={(e) => updateConfig('highValueLeadThreshold', parseInt(e.target.value, 10) || 0)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white"
              />
            </div>
            <div>
              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_premium_customer_threshold')}</label>
              <input
                type="number"
                min={0}
                value={config.premiumCustomerThreshold}
                onChange={(e) => updateConfig('premiumCustomerThreshold', parseInt(e.target.value, 10) || 0)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white"
              />
            </div>
            <div>
              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_standard_customer_threshold')}</label>
              <input
                type="number"
                min={0}
                value={config.standardCustomerThreshold}
                onChange={(e) => updateConfig('standardCustomerThreshold', parseInt(e.target.value, 10) || 0)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white"
              />
            </div>
            <div>
              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_default_country')}</label>
              <input
                type="text"
                value={config.defaultCountry}
                onChange={(e) => updateConfig('defaultCountry', e.target.value)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white"
              />
            </div>
            <div>
              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_default_phone_prefix')}</label>
              <input
                type="text"
                value={config.defaultPhonePrefix}
                onChange={(e) => updateConfig('defaultPhonePrefix', e.target.value)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white"
              />
            </div>
          </div>
        </div>
      </>
    ),

    campaigns_communication: (
      <>
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 space-y-6">
          <h2 className="text-lg font-semibold text-white">{p('section_campaigns')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <CheckboxField
              checked={config.enableCampaigns}
              onChange={(v) => updateConfig('enableCampaigns', v)}
              label={p('label_enable_campaigns')}
            />
            <CheckboxField
              checked={config.trackCampaignROI}
              onChange={(v) => updateConfig('trackCampaignROI', v)}
              label={p('label_track_roi')}
            />
            <CheckboxField
              checked={config.autoSegmentation}
              onChange={(v) => updateConfig('autoSegmentation', v)}
              label={p('label_auto_segmentation')}
            />
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 space-y-6">
          <h2 className="text-lg font-semibold text-white">{p('section_communication')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <CheckboxField
              checked={config.emailIntegration}
              onChange={(v) => updateConfig('emailIntegration', v)}
              label={p('label_email_integration')}
            />
            <CheckboxField
              checked={config.smsIntegration}
              onChange={(v) => updateConfig('smsIntegration', v)}
              label={p('label_sms_integration')}
            />
            <CheckboxField
              checked={config.callTracking}
              onChange={(v) => updateConfig('callTracking', v)}
              label={p('label_call_tracking')}
            />
            <CheckboxField
              checked={config.activityReminders}
              onChange={(v) => updateConfig('activityReminders', v)}
              label={p('label_activity_reminders')}
            />
          </div>
        </div>
      </>
    ),

    preferences: (
      <>
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 space-y-6">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Bell className="h-5 w-5" />
            {p('section_notifications')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <CheckboxField
              checked={config.notifyNewLead}
              onChange={(v) => updateConfig('notifyNewLead', v)}
              label={p('label_notify_new_lead')}
            />
            <CheckboxField
              checked={config.notifyDealWon}
              onChange={(v) => updateConfig('notifyDealWon', v)}
              label={p('label_notify_deal_won')}
            />
            <CheckboxField
              checked={config.notifyGoalAchieved}
              onChange={(v) => updateConfig('notifyGoalAchieved', v)}
              label={p('label_notify_goal_achieved')}
            />
            <CheckboxField
              checked={config.notifyFollowUpDue}
              onChange={(v) => updateConfig('notifyFollowUpDue', v)}
              label={p('label_notify_follow_up')}
            />
            <CheckboxField
              checked={config.emailNotifications}
              onChange={(v) => updateConfig('emailNotifications', v)}
              label={p('label_email_notifications')}
            />
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 space-y-6">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Shield className="h-5 w-5" />
            {p('section_security')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <CheckboxField
              checked={config.dataEncryption}
              onChange={(v) => updateConfig('dataEncryption', v)}
              label={p('label_data_encryption')}
            />
            <CheckboxField
              checked={config.restrictClientData}
              onChange={(v) => updateConfig('restrictClientData', v)}
              label={p('label_restrict_client_data')}
            />
            <CheckboxField
              checked={config.auditTrail}
              onChange={(v) => updateConfig('auditTrail', v)}
              label={p('label_audit_trail')}
            />
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 space-y-6">
          <h2 className="text-lg font-semibold text-white">{p('section_display')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_default_view')}</label>
              <CustomSelect
                value={config.defaultView}
                onChange={(v) => updateConfig('defaultView', v)}
                options={defaultViewOptions}
              />
            </div>
            <div>
              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_items_per_page')}</label>
              <input
                type="number"
                min={10}
                max={500}
                value={config.itemsPerPage}
                onChange={(e) => updateConfig('itemsPerPage', parseInt(e.target.value, 10) || 50)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white"
              />
            </div>
            <div className="flex flex-col gap-3 justify-center">
              <CheckboxField
                checked={config.showRevenue}
                onChange={(v) => updateConfig('showRevenue', v)}
                label={p('label_show_revenue')}
              />
            </div>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 space-y-6">
          <h2 className="text-lg font-semibold text-white">{p('section_references')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { prefix: 'leadReferencePrefix', auto: 'autoGenerateLeadReference', label: p('label_lead_prefix') },
              { prefix: 'contactReferencePrefix', auto: 'autoGenerateContactReference', label: p('label_contact_prefix') },
              { prefix: 'opportunityReferencePrefix', auto: 'autoGenerateOpportunityReference', label: p('label_opportunity_prefix') },
              { prefix: 'campaignReferencePrefix', auto: 'autoGenerateCampaignReference', label: p('label_campaign_prefix') },
              { prefix: 'interactionReferencePrefix', auto: 'autoGenerateInteractionReference', label: p('label_interaction_prefix') },
              { prefix: 'salesGoalReferencePrefix', auto: 'autoGenerateSalesGoalReference', label: p('label_sales_goal_prefix') },
            ].map(({ prefix, auto, label }) => (
              <div key={prefix} className="space-y-2">
                <label className="block w-full text-white/70 text-sm font-medium text-right">{label}</label>
                <input
                  type="text"
                  value={config[prefix]}
                  onChange={(e) => updateConfig(prefix, e.target.value.toUpperCase())}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white font-mono"
                />
                <CheckboxField
                  checked={config[auto]}
                  onChange={(v) => updateConfig(auto, v)}
                  label={p('label_auto_generate_ref')}
                />
              </div>
            ))}
          </div>
        </div>
      </>
    ),
  };

  const tabs = [
    { id: 'leads_pipeline', label: p('tab_leads_pipeline'), icon: Users },
    { id: 'goals_thresholds', label: p('tab_goals_thresholds'), icon: Target },
    { id: 'campaigns_communication', label: p('tab_campaigns_communication'), icon: Megaphone },
    { id: 'preferences', label: p('tab_preferences'), icon: Shield },
  ];

  return (
    <div className="employees-container">
      <ModuleConfigPageLayout
        title={p('title')}
        subtitle={p('subtitle')}
        titleIcon={Settings}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabContent={tabContent}
        saved={saved}
        savedMessage={p('saved_success')}
        loading={loading}
        saving={saving}
        onSave={handleSave}
        onReset={handleReset}
        headerActions={
          <button
            type="button"
            onClick={reloadConfig}
            className="refresh-button px-4 py-3 rounded-xl bg-white/5 text-white/70 hover:text-white hover:bg-white/10"
            title={t('common.refresh')}
          >
            <RefreshCcw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
        }
      >
        {statsGrid}
      </ModuleConfigPageLayout>
    </div>
  );
}
